/**
 * Winds aloft on the hangar screen. The bulletin stores feet above mean sea level, at the levels it
 * publishes (often 3,000, 6,000, 9,000, 12,000, 18,000). The screen shows every thousand feet above the
 * field, from 1,000 through 13,000, and nothing higher. Surface is not a box: the readings above already
 * show it.
 *
 * A step between two bulletin levels is read off the straight line between them (direction takes the short
 * way around the compass). A step below the lowest level, or above the highest, is left blank: the
 * bulletin did not forecast it, and the screen does not invent one. Light and variable counts as no speed
 * and no direction.
 */
import { formatTemperature, formatWind } from '@skyweather/weather-core'
import { localTime, windUnit } from './format.js'
import type { SiteUnits, WindsAloftItem, WindsAloftLevel } from './types.js'
import { WindArrow } from './WindArrow.js'

/** Thousand-foot steps above the field, 1,000 through 13,000. */
export const ALOFT_AGL_FT = [1000, 2000, 3000, 4000, 5000, 6000, 7000, 8000, 9000, 10000, 11000, 12000, 13000] as const

/** A bulletin level this close to a step (feet) is that step, not an in-between reading. */
const EXACT_FT = 100

export interface AloftStep {
  aglFt: number
  windDirDeg: number | null
  windKt: number | null
  lightAndVariable: boolean
  tempC: number | null
}

/** The bulletin to show: the one in use now, else the next to come into use, else the latest. */
export function chooseBulletin(items: readonly WindsAloftItem[], now: number): WindsAloftItem | null {
  if (items.length === 0) return null
  const inUse = items.filter((i) => i.validFrom <= now && now < i.validTo).sort((a, b) => b.issuedAt - a.issuedAt)
  if (inUse[0]) return inUse[0]
  const upcoming = items.filter((i) => i.validFrom > now).sort((a, b) => a.validFrom - b.validFrom)
  if (upcoming[0]) return upcoming[0]
  return [...items].sort((a, b) => b.validTo - a.validTo)[0] ?? null
}

/** "1k" … "13k". The panel's note says these are feet above the field. */
export function aglLabel(aglFt: number): string {
  return `${aglFt / 1000}k`
}

function mixAngle(from: number, to: number, t: number): number {
  const delta = ((to - from + 540) % 360) - 180
  return (from + delta * t + 360) % 360
}

function speedOf(level: WindsAloftLevel): number | null {
  if (level.lightAndVariable) return 0
  return level.windKt
}

function fromLevel(aglFt: number, level: WindsAloftLevel): AloftStep {
  return {
    aglFt,
    windDirDeg: level.lightAndVariable ? null : level.windDirDeg,
    windKt: level.lightAndVariable ? 0 : level.windKt,
    lightAndVariable: level.lightAndVariable,
    tempC: level.tempC,
  }
}

function between(aglFt: number, below: WindsAloftLevel, above: WindsAloftLevel, t: number): AloftStep {
  const belowKt = speedOf(below)
  const aboveKt = speedOf(above)
  const windKt = belowKt === null || aboveKt === null ? null : Math.round(belowKt + (aboveKt - belowKt) * t)
  const bothDirected = below.windDirDeg !== null && above.windDirDeg !== null && !below.lightAndVariable && !above.lightAndVariable
  const windDirDeg = bothDirected
    ? Math.round(mixAngle(below.windDirDeg as number, above.windDirDeg as number, t)) % 360
    : below.lightAndVariable
      ? above.windDirDeg
      : above.lightAndVariable
        ? below.windDirDeg
        : (below.windDirDeg ?? above.windDirDeg)
  const tempC = below.tempC !== null && above.tempC !== null ? below.tempC + (above.tempC - below.tempC) * t : (below.tempC ?? above.tempC)
  const lightAndVariable = (below.lightAndVariable && above.lightAndVariable) || (windKt !== null && windKt < 3 && windDirDeg === null)
  return {
    aglFt,
    windDirDeg: lightAndVariable ? null : windDirDeg,
    windKt: lightAndVariable ? 0 : windKt,
    lightAndVariable,
    tempC,
  }
}

/**
 * One box per thousand feet above the field. `elevationFt` is the field, so a bulletin level's height
 * above the field is `altitudeFt - elevationFt`. Null elevation leaves every box blank.
 */
export function aloftSteps(levels: readonly WindsAloftLevel[], elevationFt: number | null): AloftStep[] {
  const known = Number.isFinite(elevationFt)
    ? [...levels].filter((l) => Number.isFinite(l.altitudeFt)).sort((a, b) => a.altitudeFt - b.altitudeFt)
    : []
  return ALOFT_AGL_FT.map((aglFt) => {
    if (elevationFt === null || !Number.isFinite(elevationFt)) {
      return { aglFt, windDirDeg: null, windKt: null, lightAndVariable: false, tempC: null }
    }
    const msl = aglFt + elevationFt
    const exact = known.find((l) => Math.abs(l.altitudeFt - msl) <= EXACT_FT)
    if (exact) return fromLevel(aglFt, exact)
    let below: WindsAloftLevel | null = null
    let above: WindsAloftLevel | null = null
    for (const level of known) {
      if (level.altitudeFt < msl) below = level
      if (level.altitudeFt > msl && above === null) above = level
    }
    if (!below || !above) return { aglFt, windDirDeg: null, windKt: null, lightAndVariable: false, tempC: null }
    const t = (msl - below.altitudeFt) / (above.altitudeFt - below.altitudeFt)
    return between(aglFt, below, above, t)
  })
}

export function WindsAloftPanel({
  items,
  units,
  timeZone,
  now,
  elevationFt,
}: {
  items: WindsAloftItem[]
  units: SiteUnits
  timeZone: string
  now: number
  elevationFt: number | null
}) {
  const b = chooseBulletin(items, now)
  if (!b) return null
  const unit = windUnit(units)
  const temp = units.temp ?? 'C'
  const steps = aloftSteps(b.levels, elevationFt)
  return (
    <section className="aloft" aria-label="Winds aloft">
      <div className="aloft__head">
        <h2>Winds aloft · {b.label}</h2>
        <span>
          for use {localTime(b.validFrom, timeZone)}–{localTime(b.validTo, timeZone)}
          {now < b.validFrom ? ' · next period' : ''}
        </span>
      </div>
      <ol className="aloft__levels">
        {steps.map((s) => {
          const wind = s.lightAndVariable ? 'L&V' : s.windKt === null ? '—' : formatWind(s.windKt, unit, { withKnots: false })
          const dir = s.lightAndVariable || s.windDirDeg === null ? '' : `${String(s.windDirDeg).padStart(3, '0')}°`
          return (
            <li key={s.aglFt} className="aloft__level">
              <span className="fcst__time">{aglLabel(s.aglFt)}</span>
              <WindArrow
                fromDeg={s.lightAndVariable ? null : s.windDirDeg}
                size={22}
                label={s.windKt === null && !s.lightAndVariable ? 'no forecast at this height' : s.lightAndVariable ? 'light and variable' : dir ? `from ${dir}` : 'no direction'}
              />
              <strong className="fcst__wind">{wind}</strong>
              <span className="fcst__temp">{dir}</span>
              <span className="fcst__temp">{s.tempC === null ? '' : formatTemperature(s.tempC, temp)}</span>
            </li>
          )
        })}
      </ol>
      <p className="aloft__note">Altitudes in AGL</p>
    </section>
  )
}
